import { Chart } from 'chart.js';

const fmtEuro = (cents) =>
  '€ ' + (cents / 100).toLocaleString('nl-BE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtAantal = (n) => n.toLocaleString('nl-BE');

export function inschrijvingenChart() {
  return {
    chart: null,
    modus: 'aantal', // 'aantal' | 'bedrag'
    _observer: null,
    _data: null,

    init() {
      const el = document.getElementById('inschrijvingen-chart-data');
      try {
        this._data = el ? JSON.parse(el.textContent || 'null') : null;
      } catch {
        this._data = null;
      }
      this.$nextTick(() => this.render());
      this._observer = new MutationObserver(() => this.retheme());
      this._observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    },

    destroy() {
      this._observer?.disconnect();
      this.chart?.destroy();
    },

    theme() {
      const dark = document.documentElement.classList.contains('dark');
      return {
        text: dark ? '#9ca3af' : '#6b7280',
        grid: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        tooltipBg: dark ? '#1f2937' : '#111827',
      };
    },

    retheme() {
      this.chart?.destroy();
      this.chart = null;
      this.render();
    },

    toggle() {
      this.modus = this.modus === 'aantal' ? 'bedrag' : 'aantal';
      this.retheme();
    },

    render() {
      const el = document.getElementById('inschrijvingen-evolutie-canvas');
      if (!el) return;

      const d = this._data;
      const buckets = d?.buckets ?? [];
      const vorigeBuckets = d?.vorige_buckets ?? [];

      // Lege grafiek zonder JS-fout als er geen data is.
      if (!buckets.length) {
        const t = this.theme();
        this.chart = new Chart(el, {
          type: 'line',
          data: { labels: [], datasets: [] },
          options: this._baseOptions(t),
        });
        return;
      }

      const t = this.theme();
      const labels = buckets.map((b) => b.label);

      // Vorige periode: pad/trim op dezelfde lengte als huidige buckets.
      const vorigeAligned = buckets.map((_, i) => vorigeBuckets[i] ?? null);

      let datasets;
      if (this.modus === 'aantal') {
        datasets = [
          {
            label: 'Actief (aangevraagd + bevestigd)',
            data: buckets.map((b) => b.aantal_actief),
            borderColor: '#2563eb',
            backgroundColor: 'rgba(37,99,235,0.08)',
            borderWidth: 2.5,
            tension: 0.15,
            pointRadius: 3,
            pointHoverRadius: 6,
            fill: true,
            order: 1,
          },
          {
            label: 'Geannuleerd',
            data: buckets.map((b) => b.geannuleerd),
            borderColor: '#ef4444',
            backgroundColor: 'transparent',
            borderWidth: 2,
            borderDash: [5, 4],
            tension: 0.15,
            pointRadius: 2,
            pointHoverRadius: 5,
            fill: false,
            order: 2,
          },
          {
            label: 'Vorige periode (actief)',
            data: vorigeAligned.map((b) => (b !== null ? b.aantal_actief : null)),
            borderColor: t.text,
            backgroundColor: 'transparent',
            borderWidth: 1.5,
            borderDash: [3, 3],
            tension: 0.15,
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: false,
            order: 3,
            spanGaps: true,
          },
        ];
      } else {
        datasets = [
          {
            label: 'Boekingswaarde',
            data: buckets.map((b) => b.boekingswaarde_cents / 100),
            borderColor: '#2563eb',
            backgroundColor: 'rgba(37,99,235,0.08)',
            borderWidth: 2.5,
            tension: 0.15,
            pointRadius: 3,
            pointHoverRadius: 6,
            fill: true,
            order: 1,
          },
          {
            label: 'Vorige periode (boekingswaarde)',
            data: vorigeAligned.map((b) => (b !== null ? b.boekingswaarde_cents / 100 : null)),
            borderColor: t.text,
            backgroundColor: 'transparent',
            borderWidth: 1.5,
            borderDash: [3, 3],
            tension: 0.15,
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: false,
            order: 2,
            spanGaps: true,
          },
        ];
      }

      const isBedrag = this.modus === 'bedrag';
      const options = {
        ...this._baseOptions(t),
        scales: {
          x: {
            ticks: { color: t.text, maxRotation: 45 },
            grid: { display: false },
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: t.text,
              callback: isBedrag
                ? (v) => '€ ' + v.toLocaleString('nl-BE')
                : (v) => fmtAantal(v),
            },
            grid: { color: t.grid },
          },
        },
        plugins: {
          ...this._baseOptions(t).plugins,
          tooltip: {
            backgroundColor: t.tooltipBg,
            callbacks: {
              label: (ctx) => {
                const v = ctx.raw;
                if (v === null || v === undefined) return null;
                const prefix = ` ${ctx.dataset.label}: `;
                return prefix + (isBedrag ? fmtEuro(v * 100) : fmtAantal(v));
              },
            },
          },
        },
      };

      this.chart = new Chart(el, { type: 'line', data: { labels, datasets }, options });
    },

    _baseOptions(t) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            labels: { color: t.text, usePointStyle: true, pointStyle: 'circle' },
          },
        },
      };
    },
  };
}
